import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  Link,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { COVERAGE_COLUMNS } from "../constants";
import type { AllianceCoverageRow } from "../types/allianceCoverage";
import { allianceDetailTo, currentReturnTo } from "../utils/allianceNavigation";
import {
  coverageAllianceTablePctTypographyColor,
  coverageTierForPercentage,
  coverageTierStatusTooltip,
} from "../utils/coverageGrade";

const COVERAGE_MODULE_COUNT = COVERAGE_COLUMNS.length;

/** Match first column maxWidth so the sticky % column does not overlap. */
const STICKY_PCT_LEFT = { xs: 140, sm: 200, md: 260 } as const;

function rowCoverageStats(row: AllianceCoverageRow): {
  present: number;
  pct: number;
} {
  const present = COVERAGE_COLUMNS.reduce(
    (acc, col) => acc + (row.coverage?.[col.key] ? 1 : 0),
    0,
  );
  const pct =
    COVERAGE_MODULE_COUNT > 0
      ? Math.round((present / COVERAGE_MODULE_COUNT) * 100)
      : 0;
  return { present, pct };
}

interface AllianceCoverageTableProps {
  rows: AllianceCoverageRow[];
}

export function AllianceCoverageTable({ rows }: AllianceCoverageTableProps) {
  const theme = useTheme();
  const location = useLocation();
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const headBg = theme.palette.background.paper;
  /** Corners (vertical + horizontal sticky): above body sidebars and scrolling headers */
  const zCornerHead = 6;
  const zScrollHead = 4;

  if (!rows.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 4 }}>
        Nema podataka o savezima.
      </Typography>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        maxHeight: { xs: "min(55vh, 420px)", sm: 480, md: 640 },
        maxWidth: "100%",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: { xs: 620, sm: 780 } }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 700,
                fontSize: { xs: "10px", sm: "11px", md: "12px" },
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                position: "sticky",
                left: 0,
                top: 0,
                zIndex: zCornerHead,
                backgroundColor: headBg,
                backgroundImage: "none",
                minWidth: { xs: 120, sm: 168, md: 200 },
                maxWidth: { xs: 140, sm: 200, md: 260 },
                boxShadow: (t) => `4px 0 8px -4px ${t.palette.divider}`,
                px: { xs: 1, sm: 1.5 },
                borderBottom: 2,
                borderColor: "divider",
              }}
            >
              Savez
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "10px", sm: "11px", md: "12px" },
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                position: "sticky",
                left: STICKY_PCT_LEFT,
                top: 0,
                zIndex: zCornerHead,
                backgroundColor: headBg,
                backgroundImage: "none",
                minWidth: { xs: 52, sm: 56 },
                width: { xs: 52, sm: 56 },
                whiteSpace: "nowrap",
                boxShadow: (t) => `4px 0 8px -4px ${t.palette.divider}`,
                px: { xs: 0.75, sm: 1 },
                borderBottom: 2,
                borderColor: "divider",
              }}
            >
              <Tooltip title="Udio modula s podacima (prema stupcima u ovoj tablici)">
                <span>Postotak</span>
              </Tooltip>
            </TableCell>
            {COVERAGE_COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                align="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "9px", sm: "10px", md: "11px" },
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                  px: { xs: 0.5, sm: 0.75 },
                  position: "sticky",
                  top: 0,
                  zIndex: zScrollHead,
                  backgroundColor: headBg,
                  backgroundImage: "none",
                  borderBottom: 2,
                  borderColor: "divider",
                }}
              >
                <Tooltip title={`${col.label} - ${col.subtitle}`}>
                  <span>{isSm ? col.shortLabel : col.label}</span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const { present: presentCount, pct } = rowCoverageStats(row);

            return (
              <TableRow key={row._id} hover>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    boxShadow: (t) => `4px 0 8px -4px ${t.palette.divider}`,
                    minWidth: { xs: 120, sm: 168, md: 200 },
                    maxWidth: { xs: 140, sm: 200, md: 260 },
                    py: { xs: 1, sm: 1.25 },
                    px: { xs: 1, sm: 1.5 },
                    verticalAlign: "top",
                  }}
                >
                  <Link
                    component={RouterLink}
                    to={allianceDetailTo(row._id, currentReturnTo(location))}
                    variant="body2"
                    fontWeight={600}
                    color="inherit"
                    underline="hover"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.name || row.sport || "-"}
                  </Link>
                  {row.sport && row.name && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{
                        mt: 0.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.sport}
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    position: "sticky",
                    left: STICKY_PCT_LEFT,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    boxShadow: (t) => `4px 0 8px -4px ${t.palette.divider}`,
                    minWidth: { xs: 52, sm: 56 },
                    width: { xs: 52, sm: 56 },
                    py: { xs: 1, sm: 1.25 },
                    px: { xs: 0.75, sm: 1 },
                    verticalAlign: "middle",
                  }}
                >
                  <Tooltip
                    title={`${presentCount}/${COVERAGE_MODULE_COUNT} modula · ${coverageTierStatusTooltip(coverageTierForPercentage(pct))}`}
                    enterTouchDelay={400}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        color: (t) =>
                          coverageAllianceTablePctTypographyColor(pct, t),
                      }}
                    >
                      {pct}%
                    </Typography>
                  </Tooltip>
                </TableCell>
                {COVERAGE_COLUMNS.map((col) => {
                  const ok = !!row.coverage?.[col.key];
                  return (
                    <TableCell key={col.key} align="center" sx={{ px: 0.5 }}>
                      <Tooltip title={ok ? "Prisutno" : "Nedostaje"}>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            verticalAlign: "middle",
                            color: ok ? "success.main" : "action.disabled",
                          }}
                        >
                          {ok ? (
                            <CheckCircle
                              sx={{ fontSize: { xs: 18, sm: 20 } }}
                            />
                          ) : (
                            <Cancel sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
